import { Button, Form, Input, Select, TimePicker } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import StudentMultiSelect from './StudentMultiSelect';
import { REMINDER_PRESET_MINUTES, type ReminderFormValues } from './reminderFormUtils';
import { MAX_MESSAGE_LENGTH } from './types/reminder';
import type { Student } from './types/student';
import SentryWrap from 'components/Sentry';

const MINUTE_OPTIONS: { label: string; value: number | 'custom' }[] = [
  ...REMINDER_PRESET_MINUTES.map((m) => ({ label: `After ${m} minutes`, value: m })),
  { label: 'Custom Time', value: 'custom' },
];

type ReminderFormProps = {
  students: Student[];
  existingCount: number;
  onSubmit: (values: ReminderFormValues) => void;
  onCancel: () => void;
  initialValues?: Partial<ReminderFormValues>;
  isEditMode?: boolean;
};

export default function ReminderForm({
  students,
  existingCount,
  onSubmit,
  onCancel,
  initialValues,
  isEditMode = false,
}: ReminderFormProps) {
  const [showSoftReminder, setShowSoftReminder] = useState(
    !!initialValues?.softReminderMinutes
  );
  const [form] = Form.useForm<ReminderFormValues>();
  const defaultStudentIds = students.map((s) => s.id);
  const studentNameById = new Map(students.map((s) => [s.id, s.name]));
  const timePreset = Form.useWatch('timePreset', form);
  const customTime = Form.useWatch('customTime', form);
  const selectedStudents = Form.useWatch('selectedStudents', form) ?? defaultStudentIds;
  const selectedStudentNames = selectedStudents
    .map((id) => studentNameById.get(id))
    .filter((name): name is string => Boolean(name))
    .join(', ');

  const handleFinish = (values: ReminderFormValues) => {
    if (!isEditMode && !values.selectedStudents?.length) return;
    onSubmit(values);
  };

  const getCustomTimeRange = () => {
    const currentTime = dayjs();
    return {
      currentTime,
    };
  };

  const getDefaultCustomTime = () => dayjs().add(1, 'minute').startOf('minute');
  const previousTimePresetRef = useRef<ReminderFormValues['timePreset'] | undefined>(timePreset);
  const openedWithSavedCustomTimeRef = useRef(
    initialValues?.timePreset === 'custom' && initialValues?.customTime != null
  );

  useEffect(() => {
    const previousTimePreset = previousTimePresetRef.current;

    if (timePreset === 'custom' && customTime) {
      openedWithSavedCustomTimeRef.current = false;
    }

    const shouldInjectDefault =
      !isEditMode &&
      timePreset === 'custom' &&
      previousTimePreset !== 'custom' &&
      !customTime &&
      !openedWithSavedCustomTimeRef.current &&
      typeof previousTimePreset === 'number';

    if (shouldInjectDefault) {
      form.setFieldValue('customTime', getDefaultCustomTime());
    }

    previousTimePresetRef.current = timePreset;
  }, [customTime, form, isEditMode, timePreset]);

  return (
    <SentryWrap>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
        initialValues={{
          message: initialValues?.message ?? '',
          timePreset: initialValues?.timePreset ?? 5,
          customTime:
            initialValues?.customTime ??
            (initialValues?.timePreset === 'custom' ? getDefaultCustomTime() : undefined),
          softReminderMinutes: initialValues?.softReminderMinutes ?? null,
          selectedStudents: initialValues?.selectedStudents ?? defaultStudentIds,
        }}
      >
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <Form.Item
          name="message"
          label="Message"
          rules={[
            { required: true, message: 'Message is required' },
            { max: MAX_MESSAGE_LENGTH, message: `Max ${MAX_MESSAGE_LENGTH} characters` },
          ]}
        >
          <Input.TextArea
            maxLength={MAX_MESSAGE_LENGTH}
            showCount
            rows={2}
            placeholder="e.g. Submit your assignment"
          />
        </Form.Item>

        {!isEditMode && (
          <Form.Item name="timePreset" label="Reminder Time" rules={[{ required: true }]}>
            <Select
              options={MINUTE_OPTIONS}
              getPopupContainer={() => document.body}
              dropdownStyle={{ zIndex: 100000000 }}
            />
          </Form.Item>
        )}

        {!isEditMode && timePreset === 'custom' && (
          <Form.Item
            name="customTime"
            label="Custom time"
            rules={[
              {
                validator: (_, value: Dayjs) => {
                  const { currentTime } = getCustomTimeRange();
                  if (!value) return Promise.reject(new Error('Select time'));
                  if (!value.isAfter(currentTime))
                    return Promise.reject(new Error('Time must be after current time'));
                  return Promise.resolve();
                },
              },
            ]}
          >
            <TimePicker
              format="h:mm A"
              minuteStep={1}
              showNow={false}
              use12Hours
              disabledTime={() => {
                const { currentTime } = getCustomTimeRange();
                const currentHour = currentTime.hour();
                const currentMinute = currentTime.minute();

                return {
                  disabledMinutes: (selectedHour: number) => {
                    const minutes = Array.from({ length: 60 }, (_, minute) => minute);

                    if (selectedHour === currentHour) {
                      return minutes.filter((minute) => minute <= currentMinute);
                    }

                    return [];
                  },
                };
              }}
              getPopupContainer={() => document.body}
              popupStyle={{ zIndex: 100000000 }}
            />
          </Form.Item>
        )}
        {/* TODO: Soft reminder is disabled for now */}
        {/* {!showSoftReminder ? (
          <Button
            type="link"
            onClick={() => setShowSoftReminder(true)}
            style={{ padding: 0, marginBottom: 8 }}
          >
            + Add Soft Reminder
          </Button>
        ) : (
          <Form.Item
            name="softReminderMinutes"
            label="Soft reminder (before final)"
            rules={[
              {
                validator: (_, value) => {
                  if (value == null) return Promise.resolve();
                  const finalMins =
                    timePreset === 'custom' && customTime
                      ? (customTime.valueOf() - Date.now()) / (60 * 1000)
                      : (timePreset as number);
                  if (value >= finalMins)
                    return Promise.reject(
                      new Error('Soft reminder must be before final reminder')
                    );
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Select
              placeholder="Optional"
              allowClear
              options={[5, 10, 15, 20].map((m) => ({ label: `${m} minutes`, value: m }))}
              getPopupContainer={() => document.body}
              dropdownStyle={{ zIndex: 100000000 }}
            />
          </Form.Item>
        )} */}

        {!isEditMode && (
          <Form.Item
            name="selectedStudents"
            label="All students selected by default"
          >
            <StudentMultiSelect
              students={students}
              value={selectedStudents}
              onChange={(ids) =>
                form.setFieldValue(
                  'selectedStudents',
                  ids.length === 0 ? defaultStudentIds : ids
                )
              }
              allowClear
              maxTagCount={0}
              maxTagPlaceholder={
                selectedStudents.length === students.length
                  ? 'All students selected'
                  : selectedStudentNames
              }
            />
          </Form.Item>
        )}
      </div>

        <Form.Item noStyle shouldUpdate>
          {() => (
            <div style={{ display: 'flex', gap: 8, paddingTop: 8, flexShrink: 0 }}>
              <Button type="primary" htmlType="submit">
                Save Reminder
              </Button>
              <Button onClick={onCancel}>Cancel</Button>
            </div>
          )}
        </Form.Item>
      </Form>
    </SentryWrap>
  );
}
